/**
 * Common Types for DataLayer Tracking
 */

/**
 * Standard GA/GTM event names
 */
export enum GaEventType {
    ButtonClick = 'button_click',
    FormInteraction = 'form_interaction',
    FormStart = 'form_start',
    FormSuccess = 'form_success',
    PageView = 'page',
}

/**
 * Standard Form Interaction types
 */
export enum FormInteractionType {
    VIEW = 'view',
    SUBMIT = 'submit',
    RETURN = 'return',
    ERROR = 'error',
    SKIP = 'skip',
    WARNING = 'warning',
}

/**
 * Form interaction types that require form_value data
 * These interactions should include the form data in the event
 */
export const FORM_INTERACTIONS_WITH_VALUE: readonly FormInteractionType[] = [
    FormInteractionType.SUBMIT,
    FormInteractionType.ERROR,
    FormInteractionType.WARNING,
] as const;

/**
 * Form interaction types that don't require form_value data
 * These are typically navigation or view events
 */
export const FORM_INTERACTIONS_WITHOUT_VALUE: readonly FormInteractionType[] = [
    FormInteractionType.VIEW,
    FormInteractionType.RETURN,
    FormInteractionType.SKIP,
] as const;

/**
 * Helper function to check if a form interaction type requires value data
 * @param interactionType The interaction type to check
 * @returns true if the interaction requires form_value data
 */
export function requiresFormValue(interactionType: FormInteractionType): boolean {
    return FORM_INTERACTIONS_WITH_VALUE.includes(interactionType);
}

/**
 * Helper function to check if a form interaction type should not have value data
 * @param interactionType The interaction type to check
 * @returns true if the interaction should not include form_value data
 */
export function shouldOmitFormValue(interactionType: FormInteractionType): boolean {
    return FORM_INTERACTIONS_WITHOUT_VALUE.includes(interactionType);
}

/**
 * Represents different business units within an organization.
 * This enumeration is used to categorize various operational divisions.
 *
 * Enum members:
 * - General: Represents general-purpose services.
 * - Home: Represents home-related services.
 * - Move: Represents a relocation or moving services business unit.
 */
export enum BusinessUnit {
    General = 'general',
    Home = 'home',
    Move = 'move',
}

/**
 * Known business units get autocompletion via the BusinessUnit enum; brand- or
 * site-specific values the server renders (e.g. 'homedeal_nl') may be passed as
 * plain strings.
 */
export type BusinessUnitValue = BusinessUnit | (string & {});

/**
 * Standard form names used across applications
 */
export enum FormName {
    BusinessRegistration = 'business_registration',
    ProspectForm = 'prospect_form',
    ConsumerQuoteRequest = 'consumer_quote_request',
    Contact = 'contact',
}

/**
 * Base event interface for all dataLayer events
 */
export interface BaseEvent {
    ab_test_variant?: string | null;
    event: GaEventType;
    eventCallback?: () => void;
}

/**
 * Common options accepted by every event builder.
 *
 * The package resolves no variant of its own — it has no DOM or app-convention
 * default. abTestVariant:
 * - a string value always wins over the app-registered provider
 * - explicit `null` skips the provider and clears the key (for events that must
 *   never carry a variant — the key is pushed with `undefined`, because an
 *   omitted key would leave an earlier event's variant applying in GTM)
 * - absent/`undefined` defers to the provider, if one is registered
 *
 * @see setAbTestVariantProvider
 */
export interface BaseEventOptions {
    abTestVariant?: string | null;
}

/**
 * The company an event belongs to — i.e. the AUTHENTICATED company acting in a
 * logged-in context. It is deliberately identity-only: apps that carry a richer
 * company object on the same key (b2b's registration payload) stay assignable,
 * because the package describes just the field every app agrees on.
 *
 * Not to be confused with a company merely *displayed* to a visitor: b2c's mover
 * cards travel in the event's `displayValue`, not here.
 */
export interface CompanyIdentity {
    companyId: number | null;
}

/**
 * The lead an event belongs to. Identity-only, like CompanyIdentity — this is
 * what button_click, form_start and form_interaction carry.
 */
export interface LeadIdentity {
    leadUuid: string;
}

/**
 * The lead plus its descriptive attributes. Only form_success carries these —
 * the consumer lead form reports what was submitted — so they are deliberately
 * not offered on the other events, where no app sends them.
 */
export interface LeadDetails extends LeadIdentity {
    leadCategory?: string | null;
    leadCountry?: string | null;
    leadType?: string | null;
}

/**
 * Identity of the authenticated user/company, shared by events sent from
 * logged-in contexts (self-service portals, my-account pages).
 */
export interface UserIdentity {
    company?: CompanyIdentity;
    eidSha256?: string | null;
    userId?: number | string | null;
    userType?: string | null;
}

